(() => {
  if (window.__mmGloboNativeBooted) return;
  window.__mmGloboNativeBooted = true;

  const ROOT_SELECTOR = '[data-mm-globo-native]';
  const ACTIVE = 'gm-active';
  const DESKTOP_BREAKPOINT = 1025;
  const RESIZE_DEBOUNCE_MS = 120;
  const LAZY_IMAGE_SELECTOR = 'img[data-mm-globo-src]';
  const rootStates = new WeakMap();

  let currentViewportMode = null;
  let resizeTimer = null;

  const isDesktopViewport = () => window.innerWidth > DESKTOP_BREAKPOINT;
  const getViewportMode = () => (isDesktopViewport() ? 'desktop' : 'mobile');

  function setBodyFlags() {
    if (!document.body) return;
    document.body.classList.add(
      'gm-menu-installed',
      'gm-theme-version-4',
      'theme_store_id_796',
      'mm-globo-native-enabled'
    );
  }

  function getMode(root) {
    return root?.dataset?.mmGloboNative || 'desktop';
  }

  function isMobileRoot(root) {
    return getMode(root) === 'mobile';
  }

  function clearActiveState(root) {
    if (!root) return;

    root.querySelectorAll(`.${ACTIVE}`).forEach((node) => {
      node.classList.remove(ACTIVE);
    });
  }

  function loadLazyImage(image) {
    if (!image?.dataset?.mmGloboSrc) return;

    const nextSrc = image.dataset.mmGloboSrc;
    const preload = new Image();

    image.loading = 'eager';
    image.setAttribute('loading', 'eager');
    image.src = nextSrc;
    image.removeAttribute('data-mm-globo-src');
    image.classList.add('mm-globo-native-lazy-image-loaded');

    preload.decoding = 'async';
    preload.src = nextSrc;
    image.__mmGloboNativePreload = preload;
  }

  function loadLazyImages(scope) {
    if (!scope) return;

    scope.querySelectorAll(LAZY_IMAGE_SELECTOR).forEach(loadLazyImage);
  }

  function closeThemeDrawer() {
    const drawer = document.getElementById('NavDrawer');
    const closeButton = drawer?.querySelector('.js-drawer-close');
    const bodyHasDrawerOpen = document.body?.classList.contains('js-drawer-open-left');

    if (bodyHasDrawerOpen && closeButton) {
      closeButton.click();
    }

    if (!document.body) return;

    document.body.classList.remove('js-drawer-open', 'js-drawer-open-left');
    drawer?.setAttribute('aria-hidden', 'true');
  }

  function setTopLevelState(menu, item, { toggle = false } = {}) {
    if (!menu || !item) return;

    const siblings = Array.from(menu.children).filter((node) => node.classList?.contains('gm-level-0'));
    const shouldActivate = toggle ? !item.classList.contains(ACTIVE) : true;

    siblings.forEach((node) => node.classList.remove(ACTIVE));
    if (!shouldActivate) return;

    item.classList.add(ACTIVE);
    ensureFirstTabActive(item);
    loadLazyImages(item);
  }

  function ensureFirstTabActive(scope) {
    const group = scope?.querySelector('.gm-tabs');
    if (!group) return;
    if (group.querySelector(':scope > .gm-tab-links > .gm-item.gm-active')) return;

    const firstTab = group.querySelector(':scope > .gm-tab-links > .gm-item');
    if (firstTab?.dataset?.tabIndex !== undefined) {
      setActiveTab(group, firstTab.dataset.tabIndex);
    }
  }

  function setActiveTab(group, index, { toggle = false, loadImages = true } = {}) {
    if (!group || index === undefined || index === null || index === '') return;

    const tabs = Array.from(group.querySelectorAll(':scope > .gm-tab-links > .gm-item'));
    const panels = Array.from(group.querySelectorAll(':scope > .gm-tab-contents > .gm-tab-content'));
    const inlinePanels = Array.from(group.querySelectorAll(':scope > .gm-tab-links > .gm-item > .gm-tab-panel'));
    const targetTab = tabs.find((tab) => tab.dataset.tabIndex === index);
    const shouldActivate = toggle ? !targetTab?.classList.contains(ACTIVE) : true;

    tabs.forEach((tab) => tab.classList.remove(ACTIVE));
    panels.forEach((panel) => panel.classList.remove(ACTIVE));
    inlinePanels.forEach((panel) => panel.classList.remove(ACTIVE));

    if (!shouldActivate || !targetTab) return;

    targetTab.classList.add(ACTIVE);
    panels
      .filter((panel) => panel.dataset.tabIndex === index)
      .forEach((panel) => {
        panel.classList.add(ACTIVE);
        if (loadImages) loadLazyImages(panel);
      });

    const inlinePanel = targetTab.querySelector(':scope > .gm-tab-panel');
    if (inlinePanel) {
      inlinePanel.classList.add(ACTIVE);
      if (loadImages) loadLazyImages(inlinePanel);
    }
  }

  function normalizeMobileTabs(root) {
    if (!isMobileRoot(root)) return;
    if (root.dataset.mmGloboMobileTabsNormalized === 'true') return;

    root.querySelectorAll('.gm-tabs').forEach((group) => {
      const contents = group.querySelector(':scope > .gm-tab-contents');
      if (!contents) return;

      const panels = Array.from(contents.querySelectorAll(':scope > .gm-tab-content'));
      const tabs = Array.from(group.querySelectorAll(':scope > .gm-tab-links > .gm-item[data-tab-index]'));
      if (!tabs.length || !panels.length) return;

      tabs.forEach((tab) => {
        const index = tab.dataset.tabIndex;
        if (!index || tab.querySelector(':scope > .gm-tab-panel')) return;

        const panel = panels.find((node) => node.dataset.tabIndex === index);
        const panelList = panel?.querySelector(':scope > .gm-tab-panel');
        if (!panelList) return;

        panelList.dataset.tabIndex = index;
        tab.appendChild(panelList);
      });

      contents.remove();
    });

    root.dataset.mmGloboMobileTabsNormalized = 'true';
  }

  function bindTabs(root, signal) {
    root.querySelectorAll('.gm-tabs').forEach((group) => {
      const tabs = Array.from(group.querySelectorAll(':scope > .gm-tab-links > .gm-item'));
      if (!tabs.length) return;

      tabs.forEach((tab, index) => {
        const link = tab.querySelector(':scope > .gm-target');
        const retractor = tab.querySelector(':scope > .gm-target > .gm-retractor');

        const activate = ({ toggle = false, loadImages = true } = {}) => {
          if (tab.dataset.tabIndex === undefined) return;
          setActiveTab(group, tab.dataset.tabIndex, { toggle, loadImages });
        };

        if (index === 0 && !group.querySelector(':scope > .gm-tab-links > .gm-item.gm-active')) {
          activate({ loadImages: false });
        }

        tab.addEventListener('focusin', () => activate(), { signal });
        tab.addEventListener(
          'mouseenter',
          () => {
            if (!isMobileRoot(root) && isDesktopViewport()) activate();
          },
          { signal }
        );

        if (retractor) {
          retractor.addEventListener(
            'click',
            (event) => {
              event.preventDefault();
              event.stopPropagation();
              activate({ toggle: isMobileRoot(root) });
            },
            { signal }
          );
        }

        if (link) {
          link.addEventListener(
            'click',
            (event) => {
              if (event.target.closest('.gm-retractor')) return;

              const href = link.getAttribute('href');
              if (isMobileRoot(root)) {
                event.preventDefault();
                event.stopPropagation();
                activate();
                return;
              }
              if (!isDesktopViewport()) return;
              if (!href || href === '#' || href === '') {
                event.preventDefault();
                activate({ toggle: true });
              }
            },
            { signal }
          );
        }
      });
    });
  }

  function setLinkGroupState(list, groupIndex, { toggle = false } = {}) {
    if (!list || !groupIndex) return;

    const nodes = Array.from(list.querySelectorAll(':scope > .gm-item[data-link-group]'));
    if (!nodes.length) return;

    const targetHeading = nodes.find(
      (node) => node.classList.contains('gm-heading') && node.dataset.linkGroup === groupIndex
    );
    if (!targetHeading) return;

    const shouldActivate = toggle ? !targetHeading.classList.contains(ACTIVE) : true;
    nodes.forEach((node) => node.classList.remove(ACTIVE));
    if (!shouldActivate) return;

    nodes
      .filter((node) => node.dataset.linkGroup === groupIndex)
      .forEach((node) => node.classList.add(ACTIVE));
  }

  function bindLinkGroups(root, signal) {
    if (!isMobileRoot(root)) return;

    root.querySelectorAll('.gm-links').forEach((list) => {
      const headings = Array.from(list.querySelectorAll(':scope > .gm-item.gm-heading[data-link-group]'));
      if (!headings.length) return;

      headings.forEach((heading) => {
        const link = heading.querySelector(':scope > .gm-target');
        if (!link) return;

        const groupIndex = heading.dataset.linkGroup;
        const hasChildren = !!list.querySelector(
          `:scope > .gm-item[data-link-group="${groupIndex}"]:not(.gm-heading)`
        );
        if (!hasChildren) return;

        link.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            event.stopPropagation();
            setLinkGroupState(list, groupIndex, { toggle: true });
          },
          { signal }
        );
      });
    });
  }

  function bindTopLevel(root, signal) {
    const menu = root.querySelector('.gm-menu');
    if (!menu) return;

    const topItems = Array.from(menu.children).filter((node) => node.classList?.contains('gm-level-0'));
    const clear = () => topItems.forEach((item) => item.classList.remove(ACTIVE));

    topItems.forEach((item) => {
      const link = item.querySelector(':scope > .gm-target');
      const retractor = item.querySelector(':scope > .gm-target > .gm-retractor');
      if (!link || !item.classList.contains('gm-has-submenu')) return;

      item.addEventListener(
        'focusin',
        () => {
          if (!isMobileRoot(root)) setTopLevelState(menu, item);
        },
        { signal }
      );

      item.addEventListener(
        'mouseenter',
        () => {
          if (!isMobileRoot(root) && isDesktopViewport()) {
            setTopLevelState(menu, item);
          }
        },
        { signal }
      );

      if (retractor) {
        retractor.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            event.stopPropagation();
            setTopLevelState(menu, item, { toggle: true });
          },
          { signal }
        );
      }

      link.addEventListener(
        'click',
        (event) => {
          if (event.target.closest('.gm-retractor')) return;

          const href = link.getAttribute('href');
          if (isMobileRoot(root)) {
            event.preventDefault();
            event.stopPropagation();
            setTopLevelState(menu, item);
            return;
          }
          if (!isDesktopViewport()) return;
          if (!href || href === '#' || href === '') {
            event.preventDefault();
            setTopLevelState(menu, item, { toggle: true });
          }
        },
        { signal }
      );
    });

    if (!isMobileRoot(root)) {
      root.addEventListener(
        'mouseleave',
        () => {
          if (isDesktopViewport()) clear();
        },
        { signal }
      );
    }

    document.addEventListener(
      'click',
      (event) => {
        if (!root.contains(event.target)) clear();
      },
      { signal }
    );
  }

  function teardownRoot(root, { clearState = true } = {}) {
    const state = rootStates.get(root);
    if (state?.controller) {
      state.controller.abort();
    }

    rootStates.delete(root);
    root.dataset.mmGloboNativeBound = 'false';

    if (clearState) clearActiveState(root);
  }

  function initRoot(root, { force = false } = {}) {
    if (!root) return;
    if (force) teardownRoot(root);
    if (root.dataset.mmGloboNativeBound === 'true') return;

    const controller = new AbortController();

    root.dataset.mmGloboNativeBound = 'true';
    setBodyFlags();
    normalizeMobileTabs(root);
    clearActiveState(root);
    bindTabs(root, controller.signal);
    bindLinkGroups(root, controller.signal);
    bindTopLevel(root, controller.signal);

    rootStates.set(root, { controller });
  }

  function resetAllRoots({ closeDrawer = false } = {}) {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      teardownRoot(root, { clearState: true });
    });

    if (closeDrawer) closeThemeDrawer();
  }

  function initAllRoots({ force = false } = {}) {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      initRoot(root, { force });
    });
  }

  function handleViewportChange() {
    const nextViewportMode = getViewportMode();
    if (nextViewportMode === currentViewportMode) return;

    const leavingMobile = currentViewportMode === 'mobile' && nextViewportMode === 'desktop';
    currentViewportMode = nextViewportMode;

    resetAllRoots({ closeDrawer: leavingMobile });
    initAllRoots({ force: true });
  }

  function installViewportWatcher() {
    if (window.__mmGloboNativeViewportWatcherInstalled) return;
    window.__mmGloboNativeViewportWatcherInstalled = true;

    currentViewportMode = getViewportMode();

    const onResize = () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(handleViewportChange, RESIZE_DEBOUNCE_MS);
    };

    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('orientationchange', handleViewportChange);
  }

  function boot({ force = false } = {}) {
    setBodyFlags();
    initAllRoots({ force });
    installViewportWatcher();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', () => boot({ force: true }));
})();
